/**
 * useBackendSubscription — Resilient subscription hook with session preservation.
 *
 * ACCESS RULES:
 *   - accessAllowed is ONLY true when the backend explicitly confirms it.
 *   - On transient failures (timeout, network, redeploy), the hook retries
 *     up to 3 times with exponential backoff (1s, 2s, 4s).
 *   - If a user was PREVIOUSLY VERIFIED in this session (hasResolvedRef=true),
 *     transient failures preserve the last known access state — the user is
 *     NOT kicked out mid-session. The `error` field is populated so callers
 *     can show a non-blocking warning if desired.
 *   - For FIRST-TIME verification (hasResolvedRef=false), access is denied
 *     on failure (fail-closed). No fail-open for new sessions.
 *   - If the backend explicitly returns access_allowed=false, it is honoured
 *     immediately regardless of prior state (explicit revocation).
 *
 * CONCURRENCY & DEADLOCK PREVENTION:
 *   - CRITICAL: No `await` calls inside onAuthStateChange callback.
 *     Awaiting supabase.auth.getUser() inside onAuthStateChange deadlocks
 *     the auth state machine, causing repeated event firing.
 *   - Only one fetch runs at a time (isFetchingRef). Duplicates are dropped.
 *   - Auth state changes only trigger a re-fetch for meaningful events
 *     (SIGNED_IN, TOKEN_REFRESHED, SIGNED_OUT). Other events are ignored.
 *   - Previously verified users do NOT see loading=true on background refreshes.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionTierKey } from "@/lib/subscriptionTiers";
import { getCachedSubscription, setCachedSubscription, clearCachedSubscription, markCacheTemporarilyUnverifiable } from "@/lib/subscriptionCache";

export type AccessSource = "own" | "inherited" | "free" | "none";
export type SubscriptionRole = "owner" | "driver";

export interface BackendSubscription {
  tier: SubscriptionTierKey | null;
  jobLimit: number | null;
  aiEnabled: boolean;
  status: "active" | "trialing" | "cancelled" | "expired" | "past_due" | null;
  provider: "apple" | "stripe" | "free" | null;
  renewalDate: string | null;
  graceAccess: boolean;
  accessAllowed: boolean;
  accessSource: AccessSource;
  role: SubscriptionRole;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/** Returns true if the backend says user has access */
export function hasBackendAccess(sub: BackendSubscription): boolean {
  if (sub.loading) return false;
  return sub.accessAllowed;
}

/** Returns true if this user is the subscription owner (can manage billing) */
export function isSubscriptionOwner(sub: BackendSubscription): boolean {
  return sub.role === "owner";
}

const DEFAULT_DATA: Omit<BackendSubscription, "loading" | "error" | "refresh"> = {
  tier: null,
  jobLimit: null,
  aiEnabled: false,
  status: null,
  provider: null,
  renewalDate: null,
  graceAccess: false,
  accessAllowed: false,
  accessSource: "none",
  role: "owner",
};

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
function readValidCache(): import("@/lib/subscriptionCache").CachedSubscription | null {
  try {
    const raw = localStorage.getItem("tm_sub_cache");
    if (!raw) return null;
    const cached = JSON.parse(raw);
    const age = Date.now() - (cached.cachedAt || 0);
    if (age >= CACHE_TTL_MS) return null;
    if (!cached.accessAllowed) return null;

    // CRITICAL: If subscription has a renewal date in the past,
    // the cache is stale — force fresh backend check to prevent expired
    // subscriptions from granting access via cache (especially on native).
    // This applies regardless of cached status because the renewal boundary wins.
    if (cached.renewalDate) {
      const renewalTime = new Date(cached.renewalDate).getTime();
      if (renewalTime <= Date.now()) {
        return null;
      }
    }

    if (!cached.userId) return null;
    try {
      const authRaw = localStorage.getItem("sb-wfsbisepapnnnqyyekip-auth-token");
      if (authRaw) {
        const authParsed = JSON.parse(authRaw);
        const storedUserId = authParsed?.user?.id;
        if (storedUserId && storedUserId === cached.userId) {
          return cached;
        }
      }
    } catch {}
  } catch {}
  return null;
}

export function useBackendSubscription(): BackendSubscription {
  const isNativePlatform = Capacitor.isNativePlatform();
  const initialNativeCache = isNativePlatform ? readValidCache() : null;

  const [data, setData] = useState<Omit<BackendSubscription, "loading" | "error" | "refresh">>(() => {
    if (initialNativeCache) {
      return {
        tier: initialNativeCache.tier as SubscriptionTierKey | null,
        jobLimit: initialNativeCache.jobLimit,
        aiEnabled: initialNativeCache.aiEnabled,
        status: initialNativeCache.status as any,
        provider: initialNativeCache.provider as any,
        renewalDate: initialNativeCache.renewalDate,
        graceAccess: initialNativeCache.graceAccess,
        accessAllowed: initialNativeCache.accessAllowed,
        accessSource: initialNativeCache.accessSource as AccessSource,
        role: initialNativeCache.role as SubscriptionRole,
      };
    }
    return DEFAULT_DATA;
  });
  const [loading, setLoading] = useState(() => !initialNativeCache);
  const [error, setError] = useState<string | null>(null);
  const hasResolvedRef = useRef(!!initialNativeCache);
  const isFetchingRef = useRef(false);

  const fetchSubscription = useCallback(async (opts?: { force?: boolean }) => {
    const force = opts?.force === true;
    if (isFetchingRef.current) {
      console.debug("[useBackendSubscription] Skipping — fetch already in progress");
      return;
    }
    isFetchingRef.current = true;
    if (force) {
      // Cache-bust: ensure stale aiEnabled/access flags can never be served.
      try { clearCachedSubscription(); } catch {}
    }

    if (!hasResolvedRef.current) {
      setLoading(true);
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        if (hasResolvedRef.current && isNativePlatform) {
          console.warn("[useBackendSubscription] getSession() returned no user but previously verified on native — preserving session");
          setError("Session check returned no user — preserving prior access");
          isFetchingRef.current = false;
          return;
        }
        setData(DEFAULT_DATA);
        setError(null);
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      const cached = !force && isNativePlatform ? getCachedSubscription(session.user.id) : null;
      if (cached) {
        console.debug("[useBackendSubscription] Using native cached subscription (age:", Date.now() - cached.cachedAt, "ms)");
        setData({
          tier: cached.tier as SubscriptionTierKey | null,
          jobLimit: cached.jobLimit,
          aiEnabled: cached.aiEnabled,
          status: cached.status as any,
          provider: cached.provider as any,
          renewalDate: cached.renewalDate,
          graceAccess: cached.graceAccess,
          accessAllowed: cached.accessAllowed,
          accessSource: cached.accessSource as AccessSource,
          role: cached.role as SubscriptionRole,
        });
        setError(null);
        hasResolvedRef.current = true;
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      const MAX_RETRIES = 3;
      const TIMEOUT_MS = 10_000;
      let lastError: string | null = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) =>
            setTimeout(() => resolve({ data: null, error: { message: "Subscription check timed out" } }), TIMEOUT_MS)
          );

          const { data: result, error: fnError } = await Promise.race([
            supabase.functions.invoke("get-my-subscription"),
            timeoutPromise,
          ]);

          if (fnError) {
            lastError = fnError.message;
            console.warn(`[useBackendSubscription] Attempt ${attempt}/${MAX_RETRIES} failed:`, fnError.message);
            if (attempt < MAX_RETRIES) {
              await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
              continue;
            }
            if (hasResolvedRef.current && isNativePlatform) {
              console.warn("[useBackendSubscription] All retries failed on native but user had prior access — preserving session (grace state)");
              markCacheTemporarilyUnverifiable();
              setError(lastError);
              setLoading(false);
              isFetchingRef.current = false;
              return;
            }
            console.error("[useBackendSubscription] All retries failed — DENIED");
            setData(DEFAULT_DATA);
            setError(lastError);
            hasResolvedRef.current = false;
            setLoading(false);
            isFetchingRef.current = false;
            return;
          }

          if (result) {
            const resolvedData = {
              tier: result.tier as SubscriptionTierKey | null,
              jobLimit: result.job_limit,
              aiEnabled: result.ai_enabled ?? false,
              status: result.status,
              provider: result.provider,
              renewalDate: result.renewal_date,
              graceAccess: result.grace_access ?? false,
              accessAllowed: result.access_allowed ?? false,
              accessSource: result.access_source ?? "none",
              role: result.role ?? "owner",
            };

            setData(resolvedData);
            setError(null);
            hasResolvedRef.current = true;
            setLoading(false);
            isFetchingRef.current = false;

            if (isNativePlatform && session.user.id) {
              setCachedSubscription(session.user.id, {
                ...resolvedData,
                cacheState: result.access_allowed ? "verified" : "denied",
              });
            }
            return;
          }

          lastError = "Empty response from subscription check";
          console.warn(`[useBackendSubscription] Attempt ${attempt}/${MAX_RETRIES}: null result`);
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
            continue;
          }
        } catch (attemptErr: any) {
          lastError = attemptErr.message || "Request failed";
          console.warn(`[useBackendSubscription] Attempt ${attempt}/${MAX_RETRIES} exception:`, lastError);
          if (attempt < MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
            continue;
          }
        }
      }

      if (hasResolvedRef.current && isNativePlatform) {
        console.warn("[useBackendSubscription] All retries exhausted on native but preserving prior session access (grace state)");
        markCacheTemporarilyUnverifiable();
        setError(lastError);
      } else {
        console.error("[useBackendSubscription] All retries exhausted — DENIED");
        setData(DEFAULT_DATA);
        setError(lastError);
        hasResolvedRef.current = false;
      }
    } catch (err: any) {
      console.error("[useBackendSubscription] Top-level exception:", err);
      if (!hasResolvedRef.current || !isNativePlatform) {
        setData(DEFAULT_DATA);
      }
      setError(err.message || "Unexpected error checking subscription");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [isNativePlatform]);

  useEffect(() => {
    // Cache-bust on every app open so free-access users never see stale aiEnabled.
    void fetchSubscription({ force: true });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, _session) => {
      if (event === "SIGNED_OUT") {
        setData(DEFAULT_DATA);
        setError(null);
        hasResolvedRef.current = false;
        setLoading(false);
        clearCachedSubscription();
        return;
      }

      if (event === "SIGNED_IN") {
        // Always force-refresh after sign-in to wipe any prior session's cache.
        void fetchSubscription({ force: true });
      } else if (event === "TOKEN_REFRESHED") {
        void fetchSubscription();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchSubscription]);

  return {
    ...data,
    loading,
    error,
    refresh: () => fetchSubscription({ force: true }),
  };
}
